import { useState } from "react";
import { Download, ExternalLink, Eye, Loader2 } from "lucide-react";
import { devIssuesApi } from "@/features/dev_issues/api/devIssues";
import { MediaLightbox } from "@/features/chat/components/MediaLightbox";
import type { ChatMessageAttachment } from "@/features/chat/types";
import { OdtEditorScreen } from "@/features/doc_editor/components/OdtEditorScreen";
import { useEditorAvailability } from "@/features/doc_editor/hooks";
import { fileExtension, filesTabOpenAction } from "@/features/doc_editor/types";
import { triggerBrowserDownload } from "@/utils/download";
import type { IssueAttachment } from "@/features/dev_issues/types";

export const ATTACHMENT_UNAVAILABLE_MSG =
  "Plik nie jest jeszcze dostępny do pobrania — spróbuj ponownie za chwilę.";
export const ATTACHMENT_FAILED_MSG = "Nie udało się pobrać załącznika. Spróbuj ponownie.";

interface Props {
  attachment: Pick<IssueAttachment, "id" | "url" | "managed_file" | "original_name">;
  /** Klasy OPAKOWANIA (układ, kolor, tło chipa) — nazwa i przycisk pobrania siedzą w środku. */
  className?: string;
  nameClassName?: string;
  iconSize?: number;
  /** Icon before the name (comment thread) instead of after it (issue header). */
  iconFirst?: boolean;
}

/**
 * Załącznik zgłoszenia: nazwa + osobny przycisk pobrania.
 *
 * Kliknięcie w NAZWĘ otwiera plik w przeglądarce: zdjęcia (png, jpg…) w podglądzie jak
 * w GG (MediaLightbox, niezależnie od Collabory — tak jak przed jej wejściem), pozostałe
 * formaty w Collaborze na pełnym ekranie, TYLKO do odczytu (slot `issue_file`), o ile ją
 * zna (dokumenty, arkusze, prezentacje, PDF — lista z discovery serwera). Formaty spoza
 * listy (zip…) i stare wiersze bez ManagedFile pobierają się po kliknięciu jak dotąd
 * (stare zdjęcia otwierają się w podglądzie z zapisanego linku). Ikona pobrania pobiera
 * zawsze.
 *
 * Pobranie idzie przez link ważny TERAZ: `attachment.url` jest presigned linkiem S3
 * podpisanym przy pobraniu zgłoszenia i martwym po godzinie ("AccessDenied — Request
 * has expired"), a strona zgłoszenia bywa otwarta dużo dłużej. Klik (pobranie i podgląd
 * zdjęcia) prosi więc API o świeży link, a zapisany zostaje jako awaryjny dla wierszy bez
 * ManagedFile.
 */
export function AttachmentLink({
  attachment,
  className,
  nameClassName,
  iconSize = 9,
  iconFirst = false,
}: Props) {
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(false);
  const [image, setImage] = useState<ChatMessageAttachment | null>(null);
  const { data: editorAvail } = useEditorAvailability();
  const editorExt = editorAvail?.available ? editorAvail.extensions ?? null : null;
  const action = filesTabOpenAction(attachment.original_name, editorExt);
  const isImage = action === "image" && (!!attachment.managed_file || !!attachment.url);
  const canEditorPreview = action !== null && action !== "image" && !!attachment.managed_file;
  const canPreview = isImage || canEditorPreview;

  /** Runs `use` with a link that is valid now; alerts when there is none. */
  const withFreshUrl = async (use: (url: string) => void) => {
    if (busy) return;
    setBusy(true);
    try {
      let url: string | null = attachment.url;
      if (attachment.managed_file) {
        url = (await devIssuesApi.freshFileUrl(attachment.managed_file)).url;
      }
      if (!url) {
        window.alert(ATTACHMENT_UNAVAILABLE_MSG);
        return;
      }
      use(url);
    } catch {
      window.alert(ATTACHMENT_FAILED_MSG);
    } finally {
      setBusy(false);
    }
  };

  const download = () => withFreshUrl((url) => triggerBrowserDownload(url, attachment.original_name));

  const openImage = () =>
    withFreshUrl((url) => {
      const ext = fileExtension(attachment.original_name);
      setImage({
        id: attachment.id,
        original_filename: attachment.original_name,
        content_type: `image/${ext === "jpg" ? "jpeg" : ext}`,
        size_bytes: null,
        url,
      });
    });

  const openOrDownload = () => {
    if (isImage) void openImage();
    else if (canEditorPreview) setPreview(true);
    else void download();
  };

  const kindIcon = canPreview ? (
    <Eye size={iconSize} className="flex-shrink-0" />
  ) : (
    <ExternalLink size={iconSize} className="flex-shrink-0" />
  );

  return (
    <span className={className}>
      <button
        type="button"
        onClick={openOrDownload}
        disabled={busy}
        title={canPreview ? `Otwórz podgląd: ${attachment.original_name}` : attachment.original_name}
        className="flex items-center gap-1 min-w-0 hover:underline disabled:opacity-60"
      >
        {iconFirst && kindIcon}
        <span className={nameClassName}>{attachment.original_name}</span>
        {!iconFirst && kindIcon}
      </button>
      <button
        type="button"
        onClick={() => void download()}
        disabled={busy}
        title="Pobierz"
        aria-label={`Pobierz ${attachment.original_name}`}
        className="flex-shrink-0 opacity-60 hover:opacity-100 disabled:opacity-40"
      >
        {busy ? (
          <Loader2 size={iconSize + 2} className="animate-spin" />
        ) : (
          <Download size={iconSize + 2} />
        )}
      </button>
      {preview && (
        <OdtEditorScreen kind="issue_file" id={attachment.id} onClose={() => setPreview(false)} />
      )}
      {image && (
        <MediaLightbox items={[image]} index={0} onIndexChange={() => undefined} onClose={() => setImage(null)} />
      )}
    </span>
  );
}
